import { Plus, Trash2 } from 'lucide-react';
import {
  FieldArrayWithId,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ProviderType } from '@/services/types/treatment-types';

import { ProviderFormData } from './types';

interface BasicInfoTabProps {
  register: UseFormRegister<ProviderFormData>;
  setValue: UseFormSetValue<ProviderFormData>;
  watch: UseFormWatch<ProviderFormData>;
  practitionerFields: FieldArrayWithId<
    ProviderFormData,
    'practitioner_names',
    'id'
  >[];
  appendPractitioner: (value: { name: string }) => void;
  removePractitioner: (index: number) => void;
}

export const BasicInfoTab = ({
  register,
  setValue,
  watch,
  practitionerFields,
  appendPractitioner,
  removePractitioner,
}: BasicInfoTabProps) => {
  const watchedProviderType = watch('provider_type');

  const handleProviderTypeChange = (value: string) => {
    if (value === '__NONE__') {
      setValue('provider_type', null);
    } else {
      setValue('provider_type', value as ProviderType);
    }
  };

  return (
    <div className="space-y-6">
      {/* Provider Name */}
      <div className="space-y-2">
        <Label htmlFor="name" className="text-sm font-medium text-gray-900">
          Provider Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="name"
          {...register('name', { required: 'Provider name is required' })}
          placeholder="Enter provider name"
          className="w-full"
        />
        <p className="text-xs text-gray-500">
          The official name of the medical provider or organization
        </p>
      </div>

      {/* Provider Type */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-900">
          Provider Type
        </Label>
        <Select
          value={watchedProviderType || '__NONE__'}
          onValueChange={handleProviderTypeChange}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select provider type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__NONE__">None (Unset)</SelectItem>
            <SelectItem value={ProviderType.ORGANIZATION}>
              Organization (Clinic, etc.)
            </SelectItem>
            <SelectItem value={ProviderType.PRACTITIONER}>
              Individual Practitioner
            </SelectItem>
            <SelectItem value={ProviderType.HOSPITAL}>Hospital</SelectItem>
            <SelectItem value={ProviderType.EMS}>
              Emergency Medical Services (EMS)
            </SelectItem>
            <SelectItem value={ProviderType.URGENT_CARE}>
              Urgent Care
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500">
          The type of healthcare provider or facility
        </p>
      </div>

      {/* Practitioner Names */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-gray-900">
            Practitioner Names
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => appendPractitioner({ name: '' })}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Practitioner
          </Button>
        </div>

        {practitionerFields.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
            <p className="text-sm">No practitioners added yet</p>
            <p className="text-xs mt-1">
              Add individual practitioners working at this provider
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {practitionerFields.map((field, index) => (
              <div key={field.id} className="flex gap-3 items-start">
                <div className="flex-1">
                  <Input
                    {...register(`practitioner_names.${index}.name`)}
                    placeholder="Enter practitioner name"
                    className="w-full"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removePractitioner(index)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-500">
          Names of individual doctors or practitioners at this provider
        </p>
      </div>

      {/* Treatment Description */}
      <div className="space-y-2">
        <Label
          htmlFor="treatment"
          className="text-sm font-medium text-gray-900"
        >
          Treatment Description
        </Label>
        <Textarea
          id="treatment"
          {...register('treatment')}
          placeholder="Describe the type of treatment provided"
          className="w-full min-h-[100px]"
        />
        <p className="text-xs text-gray-500">
          Brief description of the treatment or services provided
        </p>
      </div>

      {/* Additional Notes */}
      <div className="space-y-2">
        <Label
          htmlFor="description"
          className="text-sm font-medium text-gray-900"
        >
          Additional Notes
        </Label>
        <Textarea
          id="description"
          {...register('description')}
          placeholder="Any additional notes about this provider"
          className="w-full min-h-[80px]"
        />
        <p className="text-xs text-gray-500">
          Any other relevant information about the provider
        </p>
      </div>
    </div>
  );
};
